/**
 * Positions a dropdown menu with fixed viewport coordinates and portals the
 * menu to document.body so it escapes overflow clipping, transform/backdrop-filter
 * containing blocks, and ancestor z-index stacking traps.
 */

const PORTAL_CLASS = 'popup-menu-portaled';

/**
 * @param {HTMLElement} host Custom element host (used for outside-click detection)
 * @param {object} options
 * @param {() => HTMLElement} [options.getMenuContainer]
 * @param {string} [options.menuSelector]
 * @param {string} [options.openClass='open']
 * @param {'left'|'right'} [options.align='left']
 * @param {number} [options.gap=8]
 * @param {() => void} [options.onClose]
 */
export function createPopupMenuPortal(_host, options) {
  const {
    openClass = 'open',
    align = 'left',
    gap = 8,
    onClose
  } = options;

  let isOpen = false;
  let currentTrigger = null;
  let currentMenu = null;
  /** @type {Record<string, string>} */
  let styleSnapshot = null;
  /** @type {{ parent: HTMLElement, nextSibling: ChildNode | null } | null} */
  let portalSnapshot = null;

  let outsideHandler = null;
  let scrollHandler = null;
  let resizeHandler = null;

  function position() {
    if (!currentTrigger || !currentMenu) {
      return;
    }
    const rect = currentTrigger.getBoundingClientRect();
    let left = rect.left;
    if (align === 'right') {
      left = rect.right - currentMenu.offsetWidth;
    }
    currentMenu.style.position = 'fixed';
    currentMenu.style.top = `${rect.bottom + gap}px`;
    currentMenu.style.left = `${left}px`;
    currentMenu.style.zIndex = '10000';
  }

  function portalMenuToBody(menu) {
    if (menu.parentElement === document.body) {
      return;
    }
    portalSnapshot = {
      parent: menu.parentElement,
      nextSibling: menu.nextSibling
    };
    menu.classList.add(PORTAL_CLASS);
    document.body.appendChild(menu);
  }

  function restoreMenuToHost(menu) {
    menu.classList.remove(PORTAL_CLASS);
    if (portalSnapshot?.parent) {
      portalSnapshot.parent.insertBefore(menu, portalSnapshot.nextSibling);
      portalSnapshot = null;
    }
  }

  function removeGlobalListeners() {
    if (outsideHandler) {
      document.removeEventListener('mousedown', outsideHandler, true);
      outsideHandler = null;
    }
    if (scrollHandler) {
      window.removeEventListener('scroll', scrollHandler, true);
      scrollHandler = null;
    }
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }
  }

  function restoreMenuStyles(menu) {
    if (styleSnapshot) {
      for (const key of ['position', 'top', 'left', 'right', 'zIndex']) {
        menu.style[key] = styleSnapshot[key] ?? '';
      }
      styleSnapshot = null;
    } else {
      menu.style.position = '';
      menu.style.top = '';
      menu.style.left = '';
      menu.style.right = '';
      menu.style.zIndex = '';
    }
  }

  function close() {
    if (!isOpen || !currentMenu) {
      removeGlobalListeners();
      return;
    }
    isOpen = false;
    const menuEl = currentMenu;
    removeGlobalListeners();
    restoreMenuStyles(menuEl);
    menuEl.classList.remove(openClass);
    restoreMenuToHost(menuEl);
    currentTrigger = null;
    currentMenu = null;
    onClose?.();
  }

  function open(trigger, menu) {
    if (!trigger || !menu) {
      return;
    }
    // If already open (unlikely), tear down without onClose — parent is reopening.
    if (isOpen && currentMenu) {
      removeGlobalListeners();
      restoreMenuStyles(currentMenu);
      currentMenu.classList.remove(openClass);
      restoreMenuToHost(currentMenu);
      currentTrigger = null;
      currentMenu = null;
      styleSnapshot = null;
      portalSnapshot = null;
      isOpen = false;
    }

    isOpen = true;
    currentTrigger = trigger;
    currentMenu = menu;

    styleSnapshot = {
      position: menu.style.position,
      top: menu.style.top,
      left: menu.style.left,
      right: menu.style.right,
      zIndex: menu.style.zIndex
    };

    portalMenuToBody(menu);
    // Set coordinates before the open class so transitions don't animate from (0,0).
    position();
    menu.classList.add(openClass);

    requestAnimationFrame(() => {
      if (currentMenu !== menu || !isOpen) {
        return;
      }
      position();
    });

    scrollHandler = () => position();
    resizeHandler = () => position();
    window.addEventListener('scroll', scrollHandler, true);
    window.addEventListener('resize', resizeHandler);

    outsideHandler = (e) => {
      const target = /** @type {Node} */ (e.target);
      if (!trigger.contains(target) && !menu.contains(target)) {
        close();
      }
    };
    document.addEventListener('mousedown', outsideHandler, true);
  }

  function getMenu() {
    return currentMenu ?? null;
  }

  return { open, close, getMenu };
}
